/**
 * Coordinación del refresh entre pestañas.
 *
 * La cookie de refresh es una por navegador y el token rota en cada uso, así
 * que dos pestañas que refrescan a la vez presentan el mismo token dos veces —
 * y eso es, literalmente, la firma de robo que el backend castiga quemando la
 * cadena y avisando al usuario (control S-14). El `inFlightRefresh` de
 * `auth-fetch.ts` deduplica dentro de una pestaña; este módulo deduplica entre
 * pestañas, que es donde nacían los falsos positivos: cada pestaña nueva
 * arranca con su propio `refreshSession()`.
 *
 * Dos piezas, ambas del mismo origen y de la misma sesión de navegador:
 *
 * - `navigator.locks`: un mutex real entre pestañas. La primera en llegar hace
 *   el POST; las demás esperan a que suelte el candado.
 * - `BroadcastChannel`: la ganadora anuncia el access token recién acuñado y
 *   las demás lo adoptan sin gastar la cookie. El token viaja de memoria a
 *   memoria — nunca toca `localStorage`, así que la regla de `stores/auth.ts`
 *   («en memoria, nunca persistido») se mantiene intacta.
 *
 * Sin `navigator.locks` (Safari < 15.4, jsdom) se degrada al comportamiento
 * anterior: solo deduplicación intra-pestaña. A propósito no hay un candado
 * casero sobre `localStorage` de repuesto: es estado compartido rozando
 * credenciales, se queda huérfano cuando muere una pestaña, y el caso residual
 * lo absorbe la ventana de gracia del backend (`12 §1.1`).
 */

export interface RefreshCoordinator {
  /** Ejecuta `fn` bajo el mutex compartido entre pestañas. */
  withLock<T>(fn: () => Promise<T>): Promise<T>;
  /** Publica a las demás pestañas el access token recién acuñado. */
  announceToken(token: string): void;
  /** Escucha los tokens que anuncian otras pestañas. Devuelve el desuscriptor. */
  onToken(listener: (token: string) => void): () => void;
}

const LOCK_NAME = 'absor:auth-refresh';
const CHANNEL_NAME = 'absor:auth-token';

export function createRefreshCoordinator(): RefreshCoordinator {
  const listeners = new Set<(token: string) => void>();

  // Perezoso: el canal se abre en el primer uso, no al importar el módulo, para
  // que un import en tests (o en un worker sin BroadcastChannel) no tenga
  // efectos secundarios.
  let channel: BroadcastChannel | null = null;
  const openChannel = (): BroadcastChannel | null => {
    if (channel === null && typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent) => {
        const token: unknown = (event.data as { token?: unknown } | null)?.token;

        if (typeof token === 'string') {
          for (const listener of listeners) {
            listener(token);
          }
        }
      };
    }

    return channel;
  };

  return {
    withLock<T>(fn: () => Promise<T>): Promise<T> {
      // `locks` falta en Safari viejo y en jsdom aunque el tipo DOM lo declare
      // siempre presente.
      const locks = typeof navigator === 'undefined' ? undefined : (navigator.locks as LockManager | undefined);

      if (!locks) {
        return fn();
      }

      // `request` resuelve con lo que devuelva el callback, pero su tipo DOM lo
      // pierde en `Promise<any>`; el genérico lo recupera.
      return locks.request(LOCK_NAME, fn) as Promise<T>;
    },

    announceToken(token: string): void {
      openChannel()?.postMessage({ token });
    },

    onToken(listener: (token: string) => void): () => void {
      openChannel();
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
  };
}

/** La instancia de la aplicación: una por pestaña, como el canal que envuelve. */
export const refreshCoordinator = createRefreshCoordinator();
